import type { SettlementLineageView, SettlementReversalBanner } from "./arRecoverySettlementSlice1c";

export const SETTLEMENT_SCOPED_TABS = [
  "eligibility",
  "fee-calculation",
  "adjustments",
  "statement",
  "billing-handoff",
  "history",
] as const;

export type SettlementScopedTab = (typeof SETTLEMENT_SCOPED_TABS)[number];

export type SettlementScopedPanels<TEligibility = unknown, TLine = unknown, TInvoice = unknown, TStatement = unknown, TAction = unknown, TAudit = unknown, TVersion = unknown> = {
  settlementId: string;
  requestId: number;
  eligibility: TEligibility[];
  lines: TLine[];
  invoiceDraft: TInvoice | null;
  statementVersions: TStatement[];
  actions: TAction[];
  audit: TAudit[];
  versions: TVersion[];
  reversalBanner: SettlementReversalBanner | null;
  lineage: SettlementLineageView | null;
};

export function emptySettlementScopedPanels<TEligibility, TLine, TInvoice, TStatement, TAction, TAudit, TVersion>(
  settlementId = "",
  requestId = 0,
): SettlementScopedPanels<TEligibility, TLine, TInvoice, TStatement, TAction, TAudit, TVersion> {
  return {
    settlementId,
    requestId,
    eligibility: [],
    lines: [],
    invoiceDraft: null,
    statementVersions: [],
    actions: [],
    audit: [],
    versions: [],
    reversalBanner: null,
    lineage: null,
  };
}

export function isSettlementScopedTab(tab: string): tab is SettlementScopedTab {
  return (SETTLEMENT_SCOPED_TABS as readonly string[]).includes(tab);
}

export function beginSettlementScope<TEligibility, TLine, TInvoice, TStatement, TAction, TAudit, TVersion>(
  settlementId: string,
  requestId: number,
): SettlementScopedPanels<TEligibility, TLine, TInvoice, TStatement, TAction, TAudit, TVersion> {
  return emptySettlementScopedPanels(settlementId, requestId);
}

export function commitSettlementScope<TEligibility, TLine, TInvoice, TStatement, TAction, TAudit, TVersion>(
  current: SettlementScopedPanels<TEligibility, TLine, TInvoice, TStatement, TAction, TAudit, TVersion>,
  settlementId: string,
  requestId: number,
  patch: Partial<Omit<SettlementScopedPanels<TEligibility, TLine, TInvoice, TStatement, TAction, TAudit, TVersion>, "settlementId" | "requestId">>,
): SettlementScopedPanels<TEligibility, TLine, TInvoice, TStatement, TAction, TAudit, TVersion> {
  if (current.settlementId !== settlementId || current.requestId !== requestId) {
    return current;
  }
  return {
    ...current,
    settlementId,
    requestId,
    ...patch,
  };
}

export function resolveSettlementScope<
  TEligibility extends { eligibilityId?: string; clientId?: string; paymentId?: string },
  TLine extends { lineId: string; settlementId?: string },
  TInvoice extends { settlementId?: string } | null,
  TStatement extends { settlementId?: string },
  TAction,
  TAudit,
  TVersion,
>(
  panels: SettlementScopedPanels<TEligibility, TLine, TInvoice, TStatement, TAction, TAudit, TVersion>,
  selectedSettlementId: string | undefined,
) {
  if (!selectedSettlementId || panels.settlementId !== selectedSettlementId) {
    return emptySettlementScopedPanels<TEligibility, TLine, TInvoice, TStatement, TAction, TAudit, TVersion>();
  }
  return {
    ...panels,
    settlementId: selectedSettlementId,
    lines: panels.lines.filter((line) => !line.settlementId || line.settlementId === selectedSettlementId),
    invoiceDraft: panels.invoiceDraft && (!panels.invoiceDraft.settlementId || panels.invoiceDraft.settlementId === selectedSettlementId)
      ? panels.invoiceDraft
      : null,
    statementVersions: panels.statementVersions.filter((item) => !item.settlementId || item.settlementId === selectedSettlementId),
  };
}

export function createSettlementRequestGuard() {
  let current = 0;
  return {
    next(settlementId: string) {
      current += 1;
      return { requestId: current, settlementId };
    },
    isCurrent(requestId: number, settlementId: string, selectedSettlementId: string | undefined) {
      return current === requestId && Boolean(settlementId) && settlementId === selectedSettlementId;
    },
    peek() {
      return current;
    },
  };
}

export function scopedEligibilityForSettlement<T extends { paymentId: string; allocationId?: string | null }>(
  eligibility: T[],
  lines: Array<{ paymentId: string; allocationId?: string | null }>,
) {
  const keys = new Set(lines.map((line) => `${line.paymentId}:${line.allocationId || ""}`));
  return eligibility.filter((row) => keys.has(`${row.paymentId}:${row.allocationId || ""}`));
}
